/**
 * WebSocket-Anbindung für Live-Updates.
 *
 * Es besteht immer höchstens eine Verbindung; ein Wechsel der Partie meldet die
 * alte ab und die neue an, statt eine zweite Verbindung zu öffnen.
 *
 * Die Verbindungs- und Erneuerungslogik (Reconnect, abgelaufenes Token) steckt
 * in `socketBase.ts` – gemeinsam mit dem Chat-Namensraum.
 */
import type {
  BgpSession,
  Company,
  GameEvent,
  Link,
  LogEntry,
  MarketOffer,
  MonitoringState,
  NewsItem,
  ObjectiveCompletion,
  ProgressSummary,
  RegimeKey,
  ServerLoadRow,
  World,
} from '@hosting-sim/core';
import { createAuthedSocket } from './socketBase';

/** Der kompakte Auszug, den der Server nach jedem Tick sendet. */
export interface LivePayload {
  meta: World['meta'];
  company: Company;
  derived: World['derived'];
  monitoring: MonitoringState;
  sessions: BgpSession[];
  /** Auslastung je Server – Quoten, keine absoluten Werte. */
  servers: ServerLoadRow[];
  links: Pick<Link, 'id' | 'name' | 'kind' | 'state' | 'capacityMbps' | 'utilizationMbps' | 'latencyMs'>[];
  events: GameEvent[];
  /** Eingehende Verkaufsangebote; sie laufen ab und müssen deshalb live sein. */
  offers: MarketOffer[];
  /** Aktuelles Ziel und Zählerstände; der volle Katalog kommt über REST. */
  progress: ProgressSummary;
  /** Kurzfassung der Weltlage – Regime, Preisindizes, Nachfragemix. */
  world: {
    regime: { key: RegimeKey; startedAtTick: number; endsAtTick: number };
    indices: Record<string, number>;
    demand: Record<string, number>;
  };
  logs: LogEntry[];
  report: {
    tick: number;
    newCustomers: number;
    lostCustomers: number;
    rejectedOrders: { product: string; reason: string }[];
    triggeredEvents: string[];
    invoicesIssued: number;
    /** In diesem Tick erfüllte Ziele – Anlass für Meldung und Freischaltung. */
    completedObjectives: ObjectiveCompletion[];
    /** Gesetzt, wenn mit diesem Tick ein Spieljahr endete. */
    closedYear: number | null;
    /** Neue Meldungen der Fachpresse. */
    news: NewsItem[];
  } | null;
}

const authed = createAuthedSocket('/sim');
let currentGameId: string | null = null;
let currentHandler: ((payload: LivePayload) => void) | null = null;

export function connectSocket(gameId: string, onTick: (payload: LivePayload) => void): void {
  currentHandler = onTick;

  const previous = authed.get();
  if (previous && currentGameId && currentGameId !== gameId) {
    previous.emit('unsubscribe', { gameId: currentGameId });
  }
  currentGameId = gameId;

  authed.connect(() => {
    const socket = authed.get();
    if (!socket) return;
    socket.off('tick');
    if (currentHandler) socket.on('tick', currentHandler);
    if (currentGameId) socket.emit('subscribe', { gameId: currentGameId });
  });
}

export function disconnectSocket(): void {
  const socket = authed.get();
  if (socket && currentGameId) socket.emit('unsubscribe', { gameId: currentGameId });
  authed.disconnect();
  currentGameId = null;
  currentHandler = null;
}

export function socketConnected(): boolean {
  return authed.connected();
}
